import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  TokenValidationResponse,
  SessionStatusResponse,
  ApiResponse,
  PlatformUser,
  ActivityLogListResponse,
  AccountAccess,
  DocumentFolder,
  CabinetDocument,
  AccountEntry,
  AccountEntryInput,
  AccountFile,
  AccountListResponse,
  AccountMonthTotal,
  Task,
  TaskBoard,
  TaskComment,
  TaskEvent,
  TaskInput,
  TaskListResponse,
  TaskReorderUpdate,
  TaskFile,
  TaskSubtask,
} from '@/types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000, // Increased to 30 seconds for instruments endpoint
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const platformToken = localStorage.getItem('platform_token');
    if (platformToken) {
      config.headers.Authorization = `Bearer ${platformToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      // An expired/invalid session token: clear it and send the user back to
      // login. Guard on a token existing so failed login attempts (no token
      // yet) don't trigger a redirect loop.
      const hadToken = localStorage.getItem('platform_token');
      if (hadToken) {
        localStorage.removeItem('platform_token');
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  // Platform Authentication
  login: async (email: string, password: string): Promise<{
    access_token: string,
    token_type: string,
    user: PlatformUser
  }> => {
    return api.post('/platform/auth/login', { email, password });
  },

  getMe: async (): Promise<PlatformUser> => {
    return api.get('/platform/auth/me');
  },

  // Records the logout in the activity log (JWTs are stateless — the real
  // "logout" is the client dropping its token). The token is passed
  // explicitly because the caller clears localStorage before the request
  // interceptor (a microtask) gets to read it.
  platformLogout: async (token: string): Promise<ApiResponse> => {
    return api.post('/platform/auth/logout', undefined, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // Broker Session Management
  validateToken: async (accessToken: string): Promise<TokenValidationResponse> => {
    return api.post('/auth/validate-token', { access_token: accessToken });
  },

  kiteCallback: async (requestToken: string): Promise<TokenValidationResponse> => {
    return api.post('/auth/kite-callback', { request_token: requestToken });
  },

  getSessionStatus: async (): Promise<SessionStatusResponse> => {
    return api.get('/auth/session-status');
  },

  disconnectBroker: async (): Promise<ApiResponse> => {
    return api.post('/auth/logout');
  },

  getAccessToken: async (): Promise<{
    status: string;
    access_token: string;
    user_id: string;
    user_name: string;
  }> => {
    return api.get('/auth/access-token');
  },
};

// User Management API (Admin Only)
export const usersApi = {
  listUsers: async (): Promise<PlatformUser[]> => {
    return api.get('/platform/users');
  },

  createUser: async (userData: any): Promise<PlatformUser> => {
    return api.post('/platform/users', userData);
  },

  deactivateUser: async (userId: number): Promise<ApiResponse> => {
    return api.delete(`/platform/users/${userId}`);
  },
};

// Activity Logs API (Admin Only)
export const activityLogsApi = {
  listLogs: async (params: {
    page?: number;
    limit?: number;
    user_id?: number;
    category?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<ActivityLogListResponse> => {
    return api.get('/platform/activity-logs', { params });
  },
};

// Account Management API (the two partners' logins only)
export const accountsApi = {
  // Never 403s — tells the sidebar/route whether to show the page.
  access: async (): Promise<AccountAccess> => {
    return api.get('/platform/accounts/access');
  },
  list: async (params: {
    category?: string;
    owner?: string; // 'joint' or a partner's email
    gst_only?: boolean;
    date_from?: string;
    date_to?: string;
    search?: string;
  } = {}): Promise<AccountListResponse> => {
    return api.get('/platform/accounts', { params });
  },
  monthly: async (params: {
    date_from: string;
    date_to: string;
    owner?: string;
  }): Promise<{ months: AccountMonthTotal[] }> => {
    return api.get('/platform/accounts/monthly', { params });
  },
  create: async (data: AccountEntryInput): Promise<AccountEntry> => {
    return api.post('/platform/accounts', data);
  },
  update: async (id: number, data: Partial<AccountEntryInput>): Promise<AccountEntry> => {
    return api.patch(`/platform/accounts/${id}`, data);
  },
  remove: async (id: number): Promise<{ deleted: number }> => {
    return api.delete(`/platform/accounts/${id}`);
  },
  // Attachments. Content-Type must be UNSET so the browser adds the multipart boundary.
  uploadFile: async (entryId: number, file: File): Promise<{ file: AccountFile }> => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/platform/accounts/${entryId}/files`, form, {
      headers: { 'Content-Type': undefined as any },
      timeout: 120000,
    });
  },
  deleteFile: async (entryId: number, fileId: number): Promise<{ deleted: number }> =>
    api.delete(`/platform/accounts/${entryId}/files/${fileId}`),
  // The bytes, fetched with the bearer token (a plain <a href> would carry none).
  // The server always sends octet-stream; the caller re-types the blob to preview it.
  fetchFile: async (entryId: number, fileId: number): Promise<Blob> =>
    api.get(`/platform/accounts/${entryId}/files/${fileId}/download`, { responseType: 'blob' }),
};

export const documentsApi = {
  folders: async (): Promise<{ folders: DocumentFolder[] }> => api.get('/platform/documents/folders'),
  createFolder: async (data: { name: string; parent_id?: number | null }): Promise<{ folder: DocumentFolder }> =>
    api.post('/platform/documents/folders', data),
  // parent_id: null moves to the top level; leave it out to keep the folder where it is.
  updateFolder: async (id: number, data: { name?: string; parent_id?: number | null }): Promise<{ folder: DocumentFolder }> =>
    api.patch(`/platform/documents/folders/${id}`, data),
  removeFolder: async (id: number): Promise<{ deleted: number }> => api.delete(`/platform/documents/folders/${id}`),
  list: async (params: { folder_id?: number; search?: string }): Promise<{ documents: CabinetDocument[] }> =>
    api.get('/platform/documents', { params }),
  // Content-Type must be UNSET so the browser adds the multipart boundary.
  upload: async (folderId: number, file: File): Promise<{ document: CabinetDocument }> => {
    const form = new FormData();
    form.append('folder_id', String(folderId));
    form.append('file', file);
    return api.post('/platform/documents', form, {
      headers: { 'Content-Type': undefined as any },
      timeout: 120000,
    });
  },
  update: async (id: number, data: { name?: string; folder_id?: number }): Promise<{ document: CabinetDocument }> =>
    api.patch(`/platform/documents/${id}`, data),
  remove: async (id: number): Promise<{ deleted: number }> => api.delete(`/platform/documents/${id}`),
  fetchFile: async (id: number): Promise<Blob> =>
    api.get(`/platform/documents/${id}/download`, { responseType: 'blob' }),
};


// Instruments API
export const instrumentsApi = {
  getNiftySpot: async (): Promise<{
    price: number;
    change: number;
    change_pct: number;
    timestamp: string;
  }> => {
    return api.get('/instruments/nifty-spot');
  },

  getNiftyExpiries: async (): Promise<{
    expiries: string[];
    nifty_spot: {
      price: number;
      change: number;
      change_pct: number;
      timestamp: string;
    };
  }> => {
    return api.get('/instruments/nifty-expiries');
  },

  getOptionLTP: async (
    strike: number,
    expiry: string,
    optionType: 'CE' | 'PE'
  ): Promise<{
    instrument_token: number;
    tradingsymbol: string;
    strike: number;
    expiry: string;
    option_type: string;
    /** Mark (NBBO mid) when a quote exists, else the last trade. */
    ltp: number;
    bid: number | null;
    ask: number | null;
    mark?: number | null;
    last_trade?: number | null;
    volume: number;
    oi: number;
  }> => {
    return api.post('/instruments/option-ltp', {
      strike,
      expiry,
      option_type: optionType
    });
  },

  getInstrumentsForExpiry: async (expiry: string): Promise<{
    status: string;
    expiry: string;
    instruments_count: number;
    nifty_spot: number;
    instruments: Array<{
      instrument_token: number;
      tradingsymbol: string;
      strike: number;
      expiry: string;
      option_type: string;
    }>;
  }> => {
    return api.get('/instruments/instruments-for-expiry', { params: { expiry } });
  },
};

// ── Skew analysis ────────────────────────────────────────────────────────
// Read-only. Every field is computed server-side from ONE batched REST
// snapshot with the engines' own IV/delta functions, so the page shows what
// the D22 band rule would actually pick — not a second implementation of it.

export interface SkewLeg {
  bid: number;
  ask: number;
  mid: number;
  spread: number;
  /** Book too wide to trust — excluded from the band walk. */
  wide: boolean;
  /** Percent, e.g. 24.83. Null when the solver could not converge. */
  iv: number | null;
  delta: number | null;
  abs_delta: number | null;
}

export interface SkewRow {
  strike: number;
  moneyness: number;
  call_buy: SkewLeg | null;
  put_buy: SkewLeg | null;
  call_sell: SkewLeg | null;
  put_sell: SkewLeg | null;
  /** Which side is OTM at this strike — the only IV worth charting. */
  otm_type: 'call' | 'put';
  otm_iv: number | null;
  otm_iv_sell: number | null;
  otm_abs_delta: number | null;
}

export interface SkewBand {
  side: 'call' | 'put';
  buy: (SkewLeg & { strike: number }) | null;
  sell: (SkewLeg & { strike: number }) | null;
  gap_pts: number | null;
  net_debit: number | null;
  /** False when no quoted grid strike in the window reached the target. */
  reached: boolean;
}

export interface SkewPayoff {
  points: Array<{ x: number; pl: number }>;
  max_profit: number | null;
  max_loss: number | null;
  breakevens: number[];
  /** Positive = paid, matching the band card's convention. */
  net_debit: number | null;
}

export interface SkewPayoffs {
  call: SkewPayoff | null;
  put: SkewPayoff | null;
  combined: SkewPayoff | null;
  /** Date the curves are evaluated at. */
  target_date: string;
  /** The earlier expiry — what target_date falls back to. */
  default_date: string;
  min_date: string;
  max_date: string;
  days_out: number;
  qty_per_unit: number;
  units: number;
  qty_total: number;
  lots: number;
  lot_size: number;
  range_pct: number;
  note: string;
}

export interface SkewAnalysis {
  asof: string;
  underlying: string;
  spot: number;
  grid: number;
  span: number;
  buy_expiry: string;
  sell_expiry: string;
  same_expiry: boolean;
  /** LC = both legs on one expiry; CC = sell later than buy. INVERTED is
   *  sell-before-buy, which is not a Nightingale shape — the band rule
   *  degenerates on it and the page warns instead of pretending. */
  shape: 'CC' | 'LC' | 'INVERTED';
  inverted: boolean;
  days_to_buy_expiry: number;
  days_to_sell_expiry: number;
  p1: number;
  p2: number;
  main_expiry: string;
  buy_mode: 'delta' | 'symmetric' | 'premium';
  anchor: 'nearer' | 'further' | 'mid' | null;
  premium_mult: number | null;
  forced_applied: boolean;
  symmetric_applied: boolean;
  /** Front-month ES. Reference only — NOT the ATM basis; see `atm`. */
  future: number | null;
  future_basis: number | null;
  /** Forward implied by put-call parity on the buy expiry — the underlying
   *  every IV and delta is priced against, and the ATM reference. */
  forward: number;
  /** Disagreement across strikes; small = the chain is self-consistent. */
  forward_spread: number;
  forward_strikes: number;
  forward_basis: number;
  sell_forward: number;
  /** ATM read off the chain: the delta-neutral straddle strike. */
  atm: {
    strike: number;
    /** The MAIN SELL expiry this straddle was priced on. */
    expiry?: string;
    days?: number;
    call_delta: number;
    put_delta: number;
    net_delta: number;
    call_iv: number | null;
    put_iv: number | null;
    iv: number | null;
    forward?: number;
    forward_spread?: number;
    straddle_premium: number;
    breakeven_low: number;
    breakeven_high: number;
    call_50_strike: number;
    put_50_strike: number;
  } | null;
  atm_ref: number;
  atm_ref_source: 'delta_neutral' | 'spot_fallback';
  quote_source: string;
  smile: SkewRow[];
  bands: { call: SkewBand; put: SkewBand };
  /** Pair-1 main sell placed at the midpoint of the two crawler buy strikes. */
  pair1: {
    strike: number;
    midpoint_raw: number;
    call_buy_strike: number;
    put_buy_strike: number;
    call_wing_pts: number;
    put_wing_pts: number;
    call_mid: number;
    put_mid: number;
    call_iv: number | null;
    put_iv: number | null;
    straddle_premium: number;
    breakeven_low: number;
    breakeven_high: number;
    /** Delta of the SHORT straddle. Zero = flat; negative = short tilt. */
    net_delta: number | null;
    offset_from_atm: number | null;
  } | null;
  payoffs: SkewPayoffs;
  summary: {
    atm_iv: number | null;
    atm_strike: number | null;
    /** Straddle premium at the ATM strike — the main sell's breakeven move. */
    atm_straddle: number | null;
    call_wing_iv: number | null;
    put_wing_iv: number | null;
    risk_reversal: number | null;
    call_distance_pts: number | null;
    put_distance_pts: number | null;
  };
}

/**
 * Trade Initiation (D40) — the placement as the owner's note states it, with
 * every case assumption an input. `preview` reads and writes nothing;
 * `execute` re-prices from a fresh snapshot and refuses on drift (D-B).
 */
export interface TILeg {
  strike: number; bid: number; ask: number; mid: number; spread: number; wide: boolean;
  iv: number | null; delta: number | null; abs_delta: number | null;
}
export interface TIGapRow {
  gap_pts: number; sell_strike: number; sell_bid: number; sell_delta: number;
  pair_cost: number; cover: number; climb: number; score: number;
  /** On the chosen MC gap only, when an extra gap is set (2026-10-09): the optimum and the points added. */
  optimum_gap_pts?: number; extra_pts?: number;
  /** Optimum + extra (2026-10-27): the gap is never narrower than this. */
  target_gap_pts?: number;
}
export interface TIExtremum { pl: number; pl_per_share: number; price: number }
export interface TIRead {
  points: { x: number; pl: number }[];
  central_max: TIExtremum | null; left_min: TIExtremum | null; right_min: TIExtremum | null;
  ratio_left: number | null; ratio_right: number | null;
  max_profit: number | null; max_loss: number | null; breakevens: number[];
}
export interface TIHop { from: number; to: number; steps: number; buy_cost: number; sell_gain: number; allowance: number }
/** What the wing climb rule does in the first minutes after Execute, on today's quotes ($/share). */
export interface TIClimbWings { put: number | null; call: number | null; peak: number | null }
export interface TIClimbPreview {
  available: boolean; reason?: string; ps?: number; note?: string;
  start?: TIClimbWings; end?: TIClimbWings; cost_per_share: number;
  climbs: {
    minute: number; method: 'lambda' | 'ps'; step: string; side: 'call' | 'put' | 'both' | null; bar: number | null;
    units: { unit: string; from: number; to: number; strikes: number; cost_per_share: number | null }[];
    cost_per_share: number | null; rise: number | null; net: number | null; wings_after: TIClimbWings;
  }[];
}
export interface TIPrecheck {
  start: number; rests_at: number; steps: number; hops: TIHop[];
  buy_paid: number; sell_gave_back: number; net_spent: number; delta_at_rest: number | null;
}
export interface TIAssumptions {
  main_expiry: string; quantity?: number; mc_count?: number; lc_count?: number;
  rho_up?: number; rho_down?: number; trigger?: number;
  /** Not inputs — one rule each since 2026-09-28: the MC buy K2 ± P0/3 on the grid; the MC gap argmax Cover × Climb;
   *  the LC buy one strike inside the MC sell and its gap the MC's rule re-read from it; the LB one inside the sell. */
  /** 2026-10-05: the PS fallback is read from these deltas per side (delta points, 0.35 = |Δ| 0.0035), skew included. */
  ps_delta_call?: number; ps_delta_put?: number;
  /** The wing climb rule's bars, saved on the new trade (docs/nightingale/wing_climb_rule.md). */
  lam_worse?: number; lam_comfort?: number; lam_profit?: number; comfort_ratio?: number;
  /** Owner 2026-10-09: points added to the MC's cover × climb gap, both sides, MC only (0–100, grid steps). */
  mc_extra_gap?: number;
}
export interface TIPlan {
  asof: string; underlying: string; spot: number; grid: number; quote_source: string;
  inputs: Required<TIAssumptions> & {
    sell_expiry: string; far_expiry?: string | null; mc_buy_expiry: string; mc_sell_expiry: string | null;
    ps_step_pts: number; hop_max_steps: number;
    /** the PS a unit opens on when nothing could be read (0.25 since 2026-09-28) */
    ps_fallback?: number;
  };
  days: { main: number; sell: number };
  forward: { forward: number; spread?: number; strikes?: number } | null;
  pair1: {
    strike: number; call_bid: number; put_bid: number; call_ask: number; put_ask: number;
    call_iv: number | null; put_iv: number | null; call_delta: number | null; put_delta: number | null;
    net_delta: number; credit_per_share: number;
  };
  atm: Record<string, any>;
  branches: Record<'up' | 'down', { k2: number; rho: number; move_pts: number; credit_3_sells: number; p0: number }>;
  placement: Record<'call' | 'put', {
    buy: TILeg; buy_target: number; walk: Record<string, any> | null; sell: TILeg; gap_pts: number;
    gap_chosen: TIGapRow; gap_at_edge: boolean; gap_table: TIGapRow[]; lb: TILeg; pair_cost: number;
    lb_target?: number; lb_walk?: Record<string, any> | null;
    lc_buy: TILeg; lc_buy_target: number; lc_walk: Record<string, any> | null; lc_sell: TILeg;
    lc_gap_pts: number; lc_gap_chosen: TIGapRow; lc_gap_at_edge: boolean; lc_pair_cost: number;
    /** 2026-10-27: the sell was pulled in and the buy with it, to keep the gap at least optimum + extra. */
    pull?: Record<string, any> | null; lc_pull?: Record<string, any> | null; lc_gap_table?: TIGapRow[];
  }>;
  ps: {
    /** 2026-10-02: one PS on every unit */
    flat?: number; derived?: Record<string, any>; derived_mc?: Record<string, any>; mc_expiry?: string;
    units: Record<'call' | 'put', {
      base_1d: number | null; mc_base_1d?: number | null; mc: number | null; lc: number | null; lb: number | null; reason?: string | null;
      /** the units (mc / lc / lb) that opened on the 0.25 fallback because nothing could be read (2026-09-28) */
      fallback?: string[];
      pair_step?: { per_step: number; steps: number; to: number; net: number; buy_cost: number; sell_gain: number; ps: number } | null;
      /** far case: the LC's own step, read on M at the MC's anchor and gap (near case: the pair step) */
      lc_step?: { per_step: number; steps: number; to: number; net: number; buy_cost: number; sell_gain: number; ps: number } | null;
      /** the delta each PS is — typed, or (blank) the delta of the PS it opened on before */
      delta?: Record<'mc' | 'lc' | 'lb', number | null>;
      /** true where the box was left blank */
      blank?: Record<'mc' | 'lc' | 'lb', boolean>;
      /** where each PS was read: the strike, its skew ratio, and whether it is past the last quoted strike */
      read?: Record<'mc' | 'lc' | 'lb', { ps: number | null; strike?: number; skew_ratio?: number; beyond?: boolean } | null>;
    }>;
    step_pts: number; per_grid_strike: Record<'call' | 'put', number | null>; per_hop: Record<'call' | 'put', number | null>;
  };
  economics: {
    straddle_credit_per_share: number;
    /** charged: the dearer branch, each LC sell bought back at K2 */
    protection_per_share: number; protection_by_branch: Record<'up' | 'down', number>;
    /** paid today: ask − bid per pair, the LB at ask; cash at open = straddle − this */
    protection_cash_per_share: number; net_credit_per_share: number;
    protection_share_of_straddle: number | null; lc_sells_credit_per_share: number;
    per_side: Record<'call' | 'put', { mc_pairs: number; lb: number; lc_pairs: number; total: number;
      lc_buyback: Partial<Record<'up' | 'down', { buyback: number; net_financing: number; lc_pairs_charged: number }>> }>;
  };
  legs: { type: string; position: string; strike: number; quantity: number; premium: number; iv: number | null; expiry: string; strategy_tag: string; unit?: string }[];
  day1: { read: TIRead };
  projection: {
    trigger: number; rho_up: number; rho_down: number; target_date: string; lc_sell_convention: string;
    branches: Record<'up' | 'down', {
      k2: number; rho: number; move_pts: number; credit_3_sells: number; p0: number; leg_count: number; read: TIRead;
      adverse_side: 'call' | 'put'; adverse_ratio: number | null; far_ratio: number | null;
      central_max_accounting: {
        credit_3_sells: number; pair1_itm_at_k2: number; p0: number; protection_paid: number; lc_sells_net_financing: number;
        intrinsic_peak: number; residual_time_value: number | null; central_max: number | null; share_of_sold: number | null;
      };
    }>;
  };
  /** archived 2026-10-02 — replaced by climb_preview */
  climb_precheck?: { by_side: Record<'call' | 'put', Record<'MC' | 'LC' | 'LB', TIPrecheck | null>>; net_spent_per_share_now: number };
  climb_preview: TIClimbPreview;
  warnings: string[];
}

/** The PS sweep (2026-09-29): every combination of the six PS — MC, LC and LB, call and put — valued along
 *  each branch's path and scored benefit − λ · PS spent. PS is what a unit may pay per share for each 5 points
 *  it climbs; each rung is a delta — the price of that delta's option — and delta 0 is off (never climbs). */
export type TISweepMarginal = number | 'free' | 'loss' | null;
export type TISweepMode = 'worst' | 'sum';
export type TISweepDial = 'mc_call' | 'lc_call' | 'lb_call' | 'mc_put' | 'lc_put' | 'lb_put';
export interface TISweepRef {
  dial: TISweepDial; side: 'call' | 'put'; kind: 'MC' | 'LC' | 'LB'; expiry: string | null;
  fallback: boolean; start: number;
  /** what the unit opens on with its box blank, and the delta that is */
  blank: { ps: number | null; delta: number | null };
  holds_up_to: number | null; holds_up_to_delta: number | null;
  rungs: { delta: number; ps: number | null; strike?: number | null; skew_ratio?: number | null; beyond?: boolean }[];
}
export interface TISweepUnit {
  count: number; day1_strike: number; day1_spend: number; k2_strike: number; to_k2_spend: number; at_peak_spend: number;
}
export interface TISweepBranchRead {
  peak: TIExtremum | null; call_wing: TIExtremum | null; put_wing: TIExtremum | null;
  loss_call: number | null; loss_put: number | null; ps_spent: number | null; peak_lost: number | null;
  units?: Record<TISweepDial, TISweepUnit>;
}
export interface TISweepCombo {
  combo: number; deltas: Record<TISweepDial, number>; ps: Record<TISweepDial, number | null>;
  valid: boolean; flags: string[];
  score: Record<TISweepMode, number | null>; benefit: Record<TISweepMode, number | null>;
  ps_spent: number | null; peak_cost: number | null; worst_ratio: number | null;
  by_branch: Record<'up' | 'down', TISweepBranchRead>;
}
export interface TISweepWhy {
  /** 'pays' (it moves), or why it stays put: 'hurts' (moving makes the wings worse), 'too_dear' (removes less
   *  than λ per $1), 'idle' (it would not move at any delta tried) */
  code: 'pays' | 'hurts' | 'too_dear' | 'idle'; text: string;
  alt_delta?: number | null; d_benefit?: number | null; d_spend?: number | null; ratio?: number | null;
  walks_day1?: boolean; on_top_rung?: boolean;
}
/** One combination read for safety: its worst loss (the deepest of the four wings), the wings, the peaks. */
export interface TISweepPoint {
  combo: number; ps_spent: number | null; worst: number | null; worst_wing: string;
  wings: Record<string, number | null>; peaks: Record<'up' | 'down', number | null>;
  deltas: Record<TISweepDial, number>; ps: Record<TISweepDial, number | null>; score_worst: number | null;
}
export interface TISweepChoice extends TISweepCombo {
  mode: TISweepMode; lam: number; plateau_size: number;
  drop_to_neighbours: { min: number | null; max: number | null };
  /** at the page's λ only: why each unit is set where it is */
  why?: Partial<Record<TISweepDial, TISweepWhy>>;
  /** at the page's λ only: the deltas found per unit among every combination within `tol` $/share of the best */
  ranges?: { tol: number; combos: number; by_dial: Partial<Record<TISweepDial, { min: number; max: number; deltas: number[] }>> };
  /** at the page's λ only: the four losses and two peaks, every unit staying put → this answer */
  wings?: { wings: { wing: string; branch: 'up' | 'down'; side: 'call' | 'put'; before: number | null; after: number | null }[];
            peaks: { branch: 'up' | 'down'; before: number | null; after: number | null }[] };
  /** at the page's λ only: a combination scoring within a couple of dollars of this one with a clearly smaller worst loss */
  safer_near?: (TISweepPoint & { score: number | null; score_gap: number | null; worst_gain: number | null; extra_ps: number | null }) | null;
}
export interface TISweepCurvePoint {
  delta: number; ps: number | null; off: boolean; chosen: boolean; valid: boolean;
  score: number | null; benefit: number | null; ps_spent: number | null; peak_cost: number | null;
  marginal: TISweepMarginal;
}
export interface TIPsSweep {
  asof: string; main_expiry: string; spot: number; lam: number; pi: { up: number; down: number };
  deltas: number[]; dials: TISweepDial[]; k2: { up: number; down: number };
  refs: Record<TISweepDial, TISweepRef>;
  anchor: { passed: boolean; by_branch: Record<'up' | 'down', { passed: boolean; diffs: Record<string, unknown> }> };
  fast_check: { passed: boolean };
  baseline: Record<'up' | 'down', {
    central_max: TIExtremum | null; left_min: TIExtremum | null; right_min: TIExtremum | null;
    ratio_left: number | null; ratio_right: number | null;
  }>;
  chosen: { lam: number; worst: TISweepChoice | null; sum: TISweepChoice | null };
  by_lambda: { lam: number; worst: TISweepChoice | null; sum: TISweepChoice | null }[];
  curves: Record<TISweepMode, Partial<Record<TISweepDial, TISweepCurvePoint[]>>>;
  top: Record<TISweepMode, TISweepCombo[]>;
  counts: { combos: number; scored: number; no_peak: number; with_reference?: number };
  /** the clock along each scenario: the market travels `sigmas_per_day` daily sigmas a day; every PS re-read from its delta */
  clock?: { timed: boolean; sigma_day_pts: number; sigmas_per_day: number; days_to_k2: Record<'up' | 'down', number>;
            days_left: number; last_days: number };
  /** how much safety each dollar buys: the lowest worst loss any combination reaches for that much PS */
  frontier: { step: number; pareto_count: number; points: (TISweepPoint & { is_answer: boolean; per_dollar: number | null })[] };
  model: { path_step_pts: number; price_points: number; repricing: string; ps: string; time?: string;
           benefit: Record<TISweepMode, string>; limitations: string[] };
  seconds: number; log_file?: string | null; table_file?: string | null; snapshot_file?: string | null;
}

export const tradeInitiationApi = {
  preview: async (params: TIAssumptions): Promise<TIPlan> =>
    api.get('/trade-initiation/preview', { params }),
  /** archived 2026-10-02 — the endpoint is gone (backend/archive/ps_sweep); kept for the archived page */
  psSweep: async (params: TIAssumptions & { lam: number; deltas?: string }): Promise<TIPsSweep> =>
    api.get('/trade-initiation/ps-sweep', { params, timeout: 180_000 }),
  execute: async (body: TIAssumptions & {
    expected?: {
      main_sell?: number; call_buy?: number; call_sell?: number; put_buy?: number; put_sell?: number;
      lb_call?: number; lb_put?: number; lc_call?: number; lc_put?: number; net_credit_per_share?: number;
    };
    accept_drift?: boolean;
  }): Promise<{
    executed: boolean; reason?: 'plan_moved' | 'write_failed' | string; drift?: string[];
    failed_step?: string; detail?: string; message?: string; trade_id?: number;
    crawler_ids?: number[]; lc_ids?: number[]; leverage_buy_id?: number;
    legs?: any[]; steps?: { step: string; status: string; [k: string]: any }[]; plan: TIPlan;
  }> => api.post('/trade-initiation/execute', body),
};


export const skewApi = {
  getExpiries: async (): Promise<{
    expiries: string[];
    spot: number | null;
    underlying: string;
    asof: string;
  }> => api.get('/skew/expiries'),

  analyse: async (params: {
    buy_expiry: string;
    sell_expiry?: string;
    span?: number;
    p1?: number;
    p2?: number;
    payoff_date?: string;
    qty?: number;
    units?: number;
    main_expiry?: string;
    buy_mode?: 'delta' | 'symmetric' | 'premium';
    anchor?: 'nearer' | 'further' | 'mid';
    premium_mult?: number;
  }): Promise<SkewAnalysis> => api.get('/skew/analysis', { params }),
};

// Candle charts API
export interface Candle {
  time: number; // epoch seconds (UTC)
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export const chartsApi = {
  getCandles: async (
    symbol: string,
    interval: string,
    opts?: { days?: number; before?: number }
  ): Promise<{
    symbol: string;
    streamer_symbol: string;
    interval: string;
    days: number;
    count: number;
    candles: Candle[];
    has_more: boolean;
    updated: string;
  }> => {
    return api.get('/charts/candles', {
      params: {
        symbol,
        interval,
        ...(opts?.days ? { days: opts.days } : {}),
        ...(opts?.before ? { before: opts.before } : {}),
      },
      timeout: 45000, // deepening fetches wait on a DXLink replay
    });
  },
};

// Nightingale API
export const nightingaleApi = {
  executeNightingale: async (spotPrice: number, tradingMode: 'manual' | 'auto' = 'manual'): Promise<{
    success: boolean;
    trade_id: number;
    trading_mode: string;
    message: string;
  }> => {
    return api.post('/nightingale/execute', {
      spot_price: spotPrice,
      trading_mode: tradingMode
    });
  },

  createManualTrade: async (spotPrice: number): Promise<{
    success: boolean;
    trade_id: number;
    message: string;
  }> => {
    return api.post('/nightingale/trades', null, { params: { spot_price: spotPrice } });
  },

  getTrades: async (status?: 'active' | 'closed'): Promise<{
    success: boolean;
    trades: any[];
    count: number;
  }> => {
    return api.get('/nightingale/trades', { params: { status } });
  },

  getTradeDetail: async (tradeId: number): Promise<{
    success: boolean;
    trade: any;
    positions: any[];
  }> => {
    return api.get(`/nightingale/trades/${tradeId}`);
  },

  closeTrade: async (tradeId: number, spotPrice: number): Promise<{
    success: boolean;
    trade_id: number;
    realized_pnl: number;
    positions_closed: number;
    message: string;
  }> => {
    return api.post(`/nightingale/trades/${tradeId}/close`, { spot_price: spotPrice });
  },

  getTradeEvents: async (tradeId: number, limit: number = 100): Promise<{
    success: boolean;
    trade_id: number;
    events: any[];
    count: number;
    /** The trade's money as its legs record it (for the audit-log ledger). */
    trade?: {
      status: string;
      realized_pnl: number;
      main_sell_realized_pnl: number;
      unrealized_pnl: number;
      open_legs: number;
      open_at_cost: number;
      legs: any[];
    } | null;
  }> => {
    return api.get(`/nightingale/trades/${tradeId}/events`, { params: { limit } });
  },

  addManualPosition: async (tradeId: number, position: {
    instrument_token: number;
    symbol: string;
    strike: number;
    expiry: string;
    option_type: string;
    position_type: string;
    quantity: number;
    entry_price: number;
  }): Promise<{
    success: boolean;
    position_id: number;
    trade_id: number;
    message: string;
  }> => {
    return api.post(`/nightingale/trades/${tradeId}/positions`, position);
  },

  addPairPosition: async (tradeId: number, pair: {
    ce_instrument_token: number;
    ce_symbol: string;
    ce_entry_price: number;
    pe_instrument_token: number;
    pe_symbol: string;
    pe_entry_price: number;
    strike: number;
    expiry: string;
    position_type: string;
    quantity: number;
  }): Promise<{
    success: boolean;
    trade_id: number;
    pair_number: number;
    ce_position_id: number;
    pe_position_id: number;
    ce_tag: string;
    pe_tag: string;
    message: string;
  }> => {
    return api.post(`/nightingale/trades/${tradeId}/pair-positions`, pair);
  },

  updatePosition: async (positionId: number, updates: {
    position_type?: string;
    quantity?: number;
    entry_price?: number;
  }): Promise<{
    success: boolean;
    position_id: number;
    trade_id: number;
    symbol: string;
    option_type: string;
    position_type: string;
    quantity: number;
    entry_price: number;
    message: string;
  }> => {
    return api.patch(`/nightingale/positions/${positionId}`, updates);
  },

  deletePosition: async (positionId: number): Promise<{
    success: boolean;
    position_id: number;
    trade_id: number;
    symbol: string;
    message: string;
  }> => {
    return api.delete(`/nightingale/positions/${positionId}`);
  },

  closePosition: async (positionId: number, exitPrice?: number): Promise<{
    success: boolean;
    position_id: number;
    trade_id: number;
    symbol: string;
    exit_price: number;
    realized_pnl: number;
    message: string;
    // Cluster-close: same-side crawler/LB legs auto-closed when a main-sell is
    // closed. Empty for non-main-sell or unpaired positions.
    cascade_closed?: Array<{
      position_id: number;
      symbol: string;
      strategy_tag: string;
      exit_price: number;
      realized_pnl: number;
    }>;
    cascade_realized_pnl?: number;
    total_realized_pnl?: number;
  }> => {
    return api.post(`/nightingale/positions/${positionId}/close`, { exit_price: exitPrice });
  },

  getAnalytics: async (): Promise<{
    success: boolean;
    net_returns: number;
    total_closed_trades: number;
    winning_trades: number;
    losing_trades: number;
    win_rate: number;
    avg_pnl: number;
    best_trade: number;
    worst_trade: number;
    gross_profit: number;
    gross_loss: number;
    profit_factor: number;
    avg_win: number;
    avg_loss: number;
    payoff_ratio: number;
    expectancy: number;
    expectancy_r: number;
    std_dev: number;
    sharpe_ratio: number;
    sortino_ratio: number;
    max_drawdown: number;
    max_drawdown_pct: number;
    recovery_factor: number;
    max_win_streak: number;
    max_loss_streak: number;
    avg_holding_hours: number;
    equity_curve: Array<{ i: number; cum: number; pnl: number; t: string | null }>;
  }> => {
    return api.get('/nightingale/analytics');
  },

  updateTradingMode: async (tradeId: number, tradingMode: 'manual' | 'auto'): Promise<{
    success: boolean;
    trade_id: number;
    serial_number: string;
    old_mode: string;
    new_mode: string;
    message: string;
  }> => {
    return api.patch(`/nightingale/trades/${tradeId}/trading-mode`, { trading_mode: tradingMode });
  },

  updateTradeTag: async (tradeId: number, tag: 'weekly' | 'biweekly' | null): Promise<{
    success: boolean;
    trade_id: number;
    serial_number: string;
    old_tag: string | null;
    new_tag: string | null;
    message: string;
  }> => {
    return api.patch(`/nightingale/trades/${tradeId}/tag`, { tag });
  },
};

// Crawler API
export const crawlerApi = {
  listCrawlers: async (status?: string): Promise<any[]> => {
    return api.get('/crawlers/', { params: { status } });
  },

  linkTrade: async (crawlerId: number, tradeId: number | null): Promise<any> => {
    return api.post(`/crawlers/${crawlerId}/link-trade/`, { trade_id: tradeId });
  },

  manualClimb: async (crawlerId: number, side: 'call' | 'put', steps: number): Promise<{
    crawler_id: number;
    side: 'call' | 'put';
    steps_requested: number;
    steps_executed: number;
    steps_skipped: { from: number; to: number; reason: string; step_index: number }[];
    from_strike: number;
    to_strike: number;
    total_cost_per_share: number;
    total_cost_inr: number;
    qty: number;
    nightingale_trade_id: number | null;
  }> => {
    return api.post(`/crawlers/${crawlerId}/manual-climb`, { side, steps });
  },
};

// Leverage Buy API
export const leverageBuyApi = {
  listLeverageBuys: async (status?: string): Promise<any[]> => {
    return api.get('/leverage-buys/', { params: { status } });
  },

  linkTrade: async (leverageBuyId: number, tradeId: number | null): Promise<any> => {
    return api.post(`/leverage-buys/${leverageBuyId}/link-trade/`, { trade_id: tradeId });
  },

  /** Manual climb: one direct jump of N strikes toward ATM on one side (1–20). */
  manualClimb: async (leverageBuyId: number, side: 'call' | 'put', steps: number): Promise<{
    leverage_buy_id: number;
    side: 'call' | 'put';
    steps_requested: number;
    steps_executed: number;
    from_strike: number;
    to_strike: number;
    total_cost_per_share: number;
    total_cost_inr: number;
    qty: number;
    nightingale_trade_id: number | null;
  }> => {
    return api.post(`/leverage-buys/${leverageBuyId}/manual-climb`, { side, steps });
  },
};

// Nightingale V2 API
export const nightingaleV2Api = {
  getTradeFullState: async (tradeId: number): Promise<any> => {
    return api.get(`/nightingale-v2/trades/${tradeId}/full-state`);
  },

  /** Last traded price of every open leg (owner 2026-11-02: payoff charts price on it when the market is closed). */
  getTradeLastTrades: async (tradeId: number): Promise<{ trade_id: number; market_open: boolean; last_trades: Record<string, number>; stale?: string[] }> => {
    return api.get(`/nightingale-v2/trades/${tradeId}/last-trades`);
  },

  getTradeMargin: async (tradeId: number): Promise<{
    success: boolean;
    trade_id: number;
    leg_count: number;
    net_margin: number;
    peak_margin: number;
    charges?: Record<string, any>;
    legs: Array<{
      symbol: string;
      strategy_tag: string | null;
      transaction_type: 'BUY' | 'SELL';
      quantity: number;
      margin: number;
    }>;
  }> => {
    return api.get(`/nightingale-v2/trades/${tradeId}/margin`);
  },

  getBasketMargin: async (legs: Array<{
    strike: number;
    expiry: string;
    option_type: 'CE' | 'PE';
    transaction_type: 'BUY' | 'SELL';
    quantity: number;
    symbol?: string;
  }>): Promise<{
    success: boolean;
    leg_count: number;
    net_margin: number;
    peak_margin: number;
    charges?: Record<string, any>;
    legs: Array<{
      symbol: string;
      transaction_type: 'BUY' | 'SELL';
      quantity: number;
      margin: number;
    }>;
    unresolved: string[];
  }> => {
    return api.post('/nightingale-v2/margin/basket', { legs });
  },

  createCrawler: async (tradeId: number, data: {
    buy_expiry: string;
    sell_expiry: string;
    strike_diff: number;
    premium_value: number;
    /** 'cc' = crawler crawler, 'lc' = leverage crawler (D8). Defaults to 'cc'. */
    crawler_type?: 'cc' | 'lc';
    /** Premium sacrifice per side (D12); omitted → both sides take premium_value. */
    ps_call?: number;
    ps_put?: number;
    quantity: number;
    auto_stop_on_expiry: boolean;
    pair_tag?: string;
    /** One id per Add-modal submission — the batch opens at one pre-checked basecamp. */
    init_batch?: string;
  }): Promise<any> => {
    return api.post(`/nightingale-v2/trades/${tradeId}/crawlers`, data);
  },

  /**
   * The trade's four premium-sacrifice values (D19): CC-call, CC-put,
   * LC-call, LC-put. A PUT fans the changed values out to every active
   * crawler of that type on the trade — the rows keep their own copy because
   * that is what the engine reads while it climbs. Leverage buys are never
   * touched, including one converted from an LC (D20).
   */
  getCrawlerSettings: async (tradeId: number): Promise<{
    trade_id: number;
    cc_ps_call: number; cc_ps_put: number;
    lc_ps_call: number; lc_ps_put: number;
    updated_at: string;
  }> => {
    return api.get(`/nightingale-v2/trades/${tradeId}/crawler-settings`);
  },

  updateCrawlerSettings: async (tradeId: number, data: {
    cc_ps_call?: number; cc_ps_put?: number;
    lc_ps_call?: number; lc_ps_put?: number;
  }): Promise<any> => {
    return api.put(`/nightingale-v2/trades/${tradeId}/crawler-settings`, data);
  },

  /**
   * The trade's wing climb rule settings (docs/nightingale/wing_climb_rule.md):
   * the bars per side, the comfortable ratio, the switch gap, highest net /
   * biggest rise, and whether the rule climbs this trade at all.
   */
  getClimbSettings: async (tradeId: number): Promise<ClimbSettings> => {
    return api.get(`/nightingale-v2/trades/${tradeId}/climb-settings`);
  },

  updateClimbSettings: async (tradeId: number, data: Partial<Omit<ClimbSettings, 'trade_id' | 'updated_at'>>): Promise<ClimbSettings> => {
    return api.put(`/nightingale-v2/trades/${tradeId}/climb-settings`, data);
  },

  /** Each unit's PS fallback right now from its side's PS delta (or the deltas given, to preview before saving). */
  /** Lambda Algo page: active trades and how many minutes of history each has. */
  getLambdaAlgoTrades: async (): Promise<{ trades: LambdaAlgoTrade[]; max_minutes: number }> => {
    return api.get('/nightingale-v2/lambda-algo/trades');
  },

  /** Lambda Algo page: the trade's last 10 minutes, newest first. */
  getLambdaAlgo: async (tradeId: number): Promise<{ trade_id: number; max_minutes: number; history: LambdaMinute[] }> => {
    return api.get(`/nightingale-v2/trades/${tradeId}/lambda-algo`);
  },

  getPsLive: async (tradeId: number, deltas?: { call: number; put: number }, live = false): Promise<PsLive> => {
    return api.get(`/nightingale-v2/trades/${tradeId}/ps-live`,
      { params: { ...(deltas ? { delta_call: deltas.call, delta_put: deltas.put } : {}), ...(live ? { live: true } : {}) } });
  },

  createLeverageBuy: async (tradeId: number, data: {
    buy_expiry: string;
    premium_value: number;
    /** Premium sacrifice per side (D18). */
    ps_call?: number;
    ps_put?: number;
    quantity: number;
    auto_stop_on_expiry: boolean;
    pair_tag: string;
    init_batch?: string;
  }): Promise<any> => {
    return api.post(`/nightingale-v2/trades/${tradeId}/leverage-buys`, data);
  },

  updateCrawlerPairTag: async (crawlerId: number, pairTag: string | null): Promise<any> => {
    return api.patch(`/nightingale-v2/crawlers/${crawlerId}/pair-tag`, { pair_tag: pairTag });
  },

  updateLeverageBuyPairTag: async (lbId: number, pairTag: string | null): Promise<any> => {
    return api.patch(`/nightingale-v2/leverage-buys/${lbId}/pair-tag`, { pair_tag: pairTag });
  },

  convertCrawlerToLB: async (tradeId: number, crawlerId: number): Promise<any> => {
    return api.post(`/nightingale-v2/trades/${tradeId}/convert-crawler-to-lb/${crawlerId}`);
  },

  autoAssignCrawlers: async (tradeId: number): Promise<{ assigned: number }> =>
    api.post(`/nightingale-v2/trades/${tradeId}/auto-assign-crawlers`),

  addImprovementPair: async (tradeId: number, data: {
    option_type: 'CE' | 'PE';
    sell: { instrument_token: number; symbol: string; strike: number; quantity: number; entry_price: number; expiry: string };
    buy: { instrument_token: number; symbol: string; strike: number; quantity: number; entry_price: number; expiry: string };
  }): Promise<{
    success: boolean;
    trade_id: number;
    improvement_number: number;
    sell_position_id: number;
    buy_position_id: number;
    sell_tag: string;
    buy_tag: string;
    message: string;
  }> => {
    return api.post(`/nightingale-v2/trades/${tradeId}/improvement-pairs`, data);
  },
};

export const leastActionApi = {
  getConfig: async (): Promise<any> =>
    api.get('/least-action/config'),

  getActiveTrades: async (): Promise<{
    trades: any[];
    spot: number;
    range_pct: number;
  }> => api.get('/least-action/active-trades'),

  getTradePayoff: async (tradeId: number): Promise<{
    trade_id: number;
    spot: number;
    range_pct: number;
    payoff_points: { underlying_price: number; pl: number }[];
    max_profit: number | null;
    max_loss: number | null;
    breakevens: number[];
    extrema: any;
    positions: any[];
  }> => api.get(`/least-action/trades/${tradeId}/payoff`),

  // v2 supports optional operation filter
  getSignals: async (
    tradeId: number,
    operation?: 'call' | 'put',
    limit = 100,
    offset = 0,
  ): Promise<{ signals: any[] }> =>
    api.get(`/least-action/trades/${tradeId}/signals`, {
      params: { operation, limit, offset },
    }),

  // Remove a persisted signal — clears it from the log AND drops the
  // per-cluster dedup so the cluster reappears as a targetable exit.
  deleteSignal: async (
    tradeId: number,
    signalId: number,
  ): Promise<{ deleted: boolean; signal_id: number; exited_main_strike: number }> =>
    api.delete(`/least-action/trades/${tradeId}/signals/${signalId}`),

  getTradeConfig: async (tradeId: number): Promise<{
    trade_id: number;
    range_pct: number;
    delta_threshold: number;
    shelf_threshold: number;
    call_mode: 'exit_and_sell' | 'sell_only';
    put_mode: 'exit_and_sell' | 'sell_only';
    call_include_opposite: boolean;
    put_include_opposite: boolean;
    defaults: { range_pct: number; delta_threshold: number; shelf_threshold: number };
  }> => api.get(`/least-action/trades/${tradeId}/config`),

  updateTradeConfig: async (
    tradeId: number,
    payload: {
      range_pct?: number; delta_threshold?: number; shelf_threshold?: number;
      call_mode?: 'exit_and_sell' | 'sell_only';
      put_mode?: 'exit_and_sell' | 'sell_only';
      call_include_opposite?: boolean;
      put_include_opposite?: boolean;
    },
  ): Promise<{
    trade_id: number;
    range_pct: number;
    delta_threshold: number;
    shelf_threshold: number;
    call_mode: 'exit_and_sell' | 'sell_only';
    put_mode: 'exit_and_sell' | 'sell_only';
    call_include_opposite: boolean;
    put_include_opposite: boolean;
  }> => api.patch(`/least-action/trades/${tradeId}/config`, payload),
};

// ─── Least Action — cluster-exit signals (v3.1, owner 2026-10-19) ───────────

export interface LaLeg { strike: number; expiry: string; price: number; qty: number }
export interface LaPoint { x: number; pl: number }
export interface LaExtrema { left: LaPoint; centre: LaPoint; right: LaPoint }
export interface LaExitLeg {
  id: number; tag: string | null; strike: number; expiry: string; option_type: 'CE' | 'PE';
  position_type: 'buy' | 'sell'; qty: number; entry: number; close: number; bid: number; ask: number; pl: number;
}
export interface LaImprovement {
  option_type: 'CE' | 'PE';
  sell: LaLeg;
  buy: LaLeg;
  credit: number;
  gain: number;
  centre_fall: number;
  exit_side_fall: number;
  score: number;
  ratio: number | null;
  tail_ok: boolean;
  ratio_ok: boolean;
  floor: number;
  passes: boolean;
  failed: string[];
}
export interface LaSignal {
  key: string;
  /** the exit this improvement belongs to (a package's cluster, or a free slot); each exit has a left and a right one */
  exit_key?: string;
  /** worst points after the exit (curve B) — the deeper one is the priority side (owner 2026-11-02) */
  b_worst?: { left: number; right: number } | null;
  priority?: boolean;
  kind: 'package' | 'natural';
  side: 'CE' | 'PE';
  pair: number | string | null;
  closed_at?: string | null;
  exit_side: 'left' | 'right' | null;
  target: 'left' | 'right';
  exit_cash: number;
  exit_legs?: LaExitLeg[];
  status: 'signal' | 'passing' | 'fails' | 'no candidate' | 'skipped' | 'done';
  note?: string;
  improvement?: LaImprovement;
  extrema?: { none: LaExtrema; both: LaExtrema; improvement_only: LaExtrema; exit_only: LaExtrema };
  curves?: { none: number[]; both: number[]; improvement_only: number[]; exit_only: number[] | null };
  slot?: string;
  slot_kind?: 'closed' | 'silent';
  qty?: number;
}
export interface LaExit {
  exit_key: string;
  kind: 'package' | 'natural';
  side: 'CE' | 'PE';
  pair: number | string | null;
  slot?: string | null;
  b_worst?: { left: number; right: number } | null;
  priority_side: 'left' | 'right' | null;
  left?: string;
  right?: string;
}
export interface LaSlot {
  key: string;
  kind: 'closed' | 'silent';
  pair_tag: string;
  side: 'CE' | 'PE';
  qty: number;
  time: string;
  main_id?: number;
  entry_n?: number | null;
}
export interface LaEvaluation {
  trade_id: number;
  time: string;
  spot: number;
  lambda_value: number;
  entered: number;
  tail_floor: number;
  main_expiry: string;
  buy_expiries: string[];
  qty: number;
  realized: number;
  open_legs: number;
  quotes: number;
  xs?: number[];
  extrema: LaExtrema;
  /** 'last_trade' when the market was closed (owner 2026-11-02): every leg priced on its last trade. */
  priced_on?: 'quotes' | 'last_trade';
  signals: LaSignal[];
  /** every exit with its left / right signal keys; [0] = the one whose priority-side improvement passes and scores best */
  exits?: LaExit[];
  checked?: number;
  passing?: number;
  slots?: { cap: number; entered: number; unmatched_entries: number; free: LaSlot[]; used: LaSlot[] };
}
export interface LaSnapshot {
  trade_id: number;
  lambda_value: number;
  default_lambda: number;
  entered: number;
  latest: LaEvaluation | null;
  history: LaEvaluation[];
}
export interface LaTrade {
  id: number;
  serial_number: string;
  max_pair: number;
  eligible: boolean;
  lambda_value: number;
  entered: number;
  last_run: string | null;
}

export const leastActionLambdaApi = {
  getStatus: async (): Promise<{
    running: boolean; check_second: number; default_lambda: number; history_minutes: number; window_strikes: number;
    next_expiries: number; tail_width_pct: number; tail_floors: number[]; exit_side_ratio: number; max_improvements: number;
  }> => api.get('/least-action-lambda/status'),
  getTrades: async (): Promise<{ trades: LaTrade[] }> => api.get('/least-action-lambda/trades'),
  getTrade: async (tradeId: number): Promise<LaSnapshot> => api.get(`/least-action-lambda/trades/${tradeId}`),
  setLambda: async (tradeId: number, value: number): Promise<{ trade_id: number; lambda_value: number }> =>
    api.put(`/least-action-lambda/trades/${tradeId}/lambda`, { value }),
  enter: async (tradeId: number, body: {
    kind: 'package' | 'natural'; side: 'CE' | 'PE'; pair: number | string; option_type: 'CE' | 'PE';
    exit: { id: number; price: number }[];
    sell: { strike: number; expiry: string; price: number };
    buy: { strike: number; expiry: string; price: number };
    slot?: string;
  }): Promise<{ trade_id: number; n: number; exit_realized: number; tags: string[]; cluster: string; paid_by: string }> =>
    api.post(`/least-action-lambda/trades/${tradeId}/enter`, body),
  run: async (tradeId: number): Promise<LaSnapshot> => api.post(`/least-action-lambda/trades/${tradeId}/run`),
};

// ─── Pair 2 + 3 together (range-bound trades, owner 2026-10-22) ──────────────

export interface P23Point { pl: number; price: number }
export interface P23Extrema { left_min: P23Point | null; central_max: P23Point | null; right_min: P23Point | null }
export type P23Pairs = Record<'2' | '3', { CE: number; PE: number }>;
export interface P23Row {
  pairs: P23Pairs;
  /** Owner 2026-10-29: reaches 1 : 0.7 AND the worst loss is inside the loss cap (15k per lot). */
  passes: boolean;
  reaches_ratio?: boolean;
  within_cap?: boolean;
  worst?: number;
  score: number;
  ratio: number;
  credit: number;
  climb_debit: number;
  climbs: number;
  stop_reason: string;
  before: P23Extrema;
  after: P23Extrema;
}
export interface P23UnitClimb { side: 'CE' | 'PE'; uid: string; unit: string; steps: number; from: number; to: number }
export interface P23Best extends P23Row { debit_cap: number | null; unit_climbs: P23UnitClimb[]; clusters?: Record<string, ClusterEco> }
export interface P23Result {
  trade_id: number;
  evaluated_at: string;
  status: 'recommending' | 'gated';
  reason?: string;
  spot?: number;
  rule: { window_share: number; ratio: number; k: number; debit_cap_pct: number; w: number; pairs: string };
  search?: { pair1_strike: number; atm: number; straddle: number; pair2_strikes: number[]; pair3_near: number; loss_cap: number;
             max_spread_pct: number; wide_spread: string[]; window_lo: number; window_hi: number; expiry: string; qty: number };
  stats?: { combinations: number; shortlisted: number; candidates_evaluated: number; passing: number; within_cap?: number; seconds: number; pass1_s: number; partial: boolean };
  book_extrema?: Record<string, { pl: number; price: number } | null>;
  best?: P23Best;
  top?: P23Row[];
  curves?: { book: { x: number; pl: number }[]; with_pairs: { x: number; pl: number }[]; final: { x: number; pl: number }[] };
  units?: Record<'2' | '3', { crawler_id: number; lb_from_crawler_id: number }>;
}
export interface P23Trade { id: number; serial_number: string; eligible: boolean; reason: string | null; evaluated_at: string | null; status: string | null }
export interface P23ExecuteResult { trade_id: number; success: boolean; pairs: P23Pairs; steps: { step: string; status: string; reason?: string; [k: string]: any }[] }

export const pair23Api = {
  getTrades: async (): Promise<{ trades: P23Trade[] }> => api.get('/pair23/trades'),
  getTrade: async (tradeId: number): Promise<{ trade_id: number; result: P23Result | null }> => api.get(`/pair23/trades/${tradeId}`),
  run: async (tradeId: number): Promise<{ trade_id: number; result: P23Result | null }> => api.post(`/pair23/trades/${tradeId}/run`),
  execute: async (tradeId: number, ce: [number, number], pe: [number, number]): Promise<P23ExecuteResult> =>
    api.post(`/pair23/trades/${tradeId}/execute`, { ce, pe }),
};

// ─── Crawler gap (MC sell climb-out / climb-back, owner 2026-10-23) ─────────
export interface CrawlerGapMove {
  kind: 'out' | 'back';
  time: string;
  cash: number | null;
  from_strike: number | null;
  to_strike: number | null;
}

export interface CrawlerGapRow {
  crawler_id: number;
  side: 'CE' | 'PE';
  pair_tag?: string | null;
  strike: number;
  target?: number;
  out: boolean;
  gap_pts: number;
  tier?: number;
  paid?: number;
  paused?: boolean;
  debit?: number;
  worth?: number;
  need?: number;
  bar?: number;
  note?: string;
  last_move?: CrawlerGapMove | null;
}

export interface CrawlerGapSnapshot {
  trade_id: number;
  eligible: boolean;
  enabled: boolean;
  live: boolean;
  time?: string | null;
  spot?: number;
  peak?: number | null;
  worst?: number | null;
  skip?: string | null;
  sells: CrawlerGapRow[];
}

export const crawlerGapApi = {
  getTrade: async (tradeId: number): Promise<CrawlerGapSnapshot> => api.get(`/crawler-gap/trades/${tradeId}`),
};

// ─── Cluster gate: each cluster on its own; silent clusters (owner 2026-10-26) ───
export interface ClusterEco {
  side: 'CE' | 'PE';
  pair_tag: string;
  wing: 'left' | 'right';
  main: { strike: number; price: number; qty: number };
  legs: { id: number; tag: string; unit: string; from_strike: number; strike: number; expiry: string; side: string; type: string; price: number; qty: number; iv: number }[];
  credit: number;
  protection: number;
  net: number;
  max_profit: number;
  max_loss: number;
  loss_at: number;
  ratio: number | null;
  limit: number;
  verdict: 'keep' | 'skip';
  reason: string;
}

export interface SilentCluster {
  pair_tag: string;
  side: 'CE' | 'PE';
  wing: 'left' | 'right';
  open_legs: number;
  closed_pnl: number;
  open_pnl?: number;
  legs: { id: number; tag: string; type: string; strike: number; expiry: string; qty: number; entry: number; open: boolean }[];
  units: { crawler: number[]; lb: number[] };
  now?: { credit: number; protection: number; net: number; max_profit: number; max_loss: number; ratio: number | null };
  ready?: boolean;
}

export interface SilentClustersResponse {
  trade_id: number;
  spot: number | null;
  limit: number;
  ready_ratio: number;
  clusters: SilentCluster[];
}

export const clusterGateApi = {
  silent: async (tradeId: number): Promise<SilentClustersResponse> => api.get(`/cluster-gate/trades/${tradeId}/silent`),
  permanent: async (tradeId: number, pair_tag: string, side: 'CE' | 'PE'): Promise<any> =>
    api.post(`/cluster-gate/trades/${tradeId}/permanent`, { pair_tag, side }),
};

// ─── Cluster Action (take-profit monitor) ───────────────────────────────────

export interface ClusterPnlRow {
  pair_tag: string;
  side: 'CE' | 'PE';
  closed?: boolean;
  open_pnl: number;
  realized_pnl: number;
  current_pnl: number;
  position_count: number;
  exited_count: number;
  at_max?: { max_profit: number; max_loss: number; ratio: number | null; at?: string; source?: string } | null;
  at_now?: { max_profit: number; max_loss: number; ratio: number | null } | null;
}
/** The trade's Least Action cluster (owner 2026-11-02): every la-sell-N / la-buy-N leg, apart from the pairs. */
export interface LeastActionPnlItem {
  n: number; paid_by: { pair_tag: string; side: 'CE' | 'PE' } | null;
  open_pnl: number; realized_pnl: number; current_pnl: number; position_count: number; exited_count: number;
}
export interface LeastActionPnl {
  open_pnl: number; realized_pnl: number; current_pnl: number; position_count: number; exited_count: number;
  items: LeastActionPnlItem[];
}
export interface TradeClusterPnl {
  trade_id: number; spot: number; realized_since_live: number; clusters: ClusterPnlRow[]; least_action?: LeastActionPnl;
}

export const clusterActionApi = {
  getConfig: async (): Promise<any> =>
    api.get('/cluster-action/config'),

  /** Owner 2026-10-29: realized since live / open / net per cluster for the trade card. */
  getTradeClusterPnl: async (tradeId: number): Promise<TradeClusterPnl> => api.get(`/cluster-action/trades/${tradeId}/cluster-pnl`),

  getActiveClusters: async (): Promise<{
    trades: any[];
    spot: number;
    global_default_pct: number;
    default_threshold_pct: number;
    min_threshold_pct: number;
    max_threshold_pct: number;
  }> => api.get('/cluster-action/active-clusters'),

  getSettings: async (): Promise<{
    global_default_pct: number;
    min_threshold_pct: number;
    max_threshold_pct: number;
  }> => api.get('/cluster-action/settings'),

  updateSettings: async (
    default_threshold_pct: number,
  ): Promise<{ global_default_pct: number }> =>
    api.patch('/cluster-action/settings', { default_threshold_pct }),

  resetTradeConfig: async (
    tradeId: number,
  ): Promise<{ trade_id: number; reset: boolean; capture_threshold_pct: number }> =>
    api.delete(`/cluster-action/trades/${tradeId}/config`),

  getTradeConfig: async (tradeId: number): Promise<{
    trade_id: number;
    capture_threshold_pct: number;
    default_threshold_pct: number;
    min_threshold_pct: number;
    max_threshold_pct: number;
  }> => api.get(`/cluster-action/trades/${tradeId}/config`),

  updateTradeConfig: async (
    tradeId: number,
    capture_threshold_pct: number,
  ): Promise<{ trade_id: number; capture_threshold_pct: number }> =>
    api.patch(`/cluster-action/trades/${tradeId}/config`, { capture_threshold_pct }),

  getSignals: async (
    tradeId: number,
    limit = 100,
    offset = 0,
  ): Promise<{ signals: any[] }> =>
    api.get(`/cluster-action/trades/${tradeId}/signals`, { params: { limit, offset } }),

  closeCluster: async (
    tradeId: number,
    pair_tag: string,
    side: 'CE' | 'PE',
  ): Promise<{
    success: boolean;
    trade_id: number;
    pair_tag: string;
    side: string;
    anchor_position_id: number | null;
    closed: any[];
    realized_pnl: number;
  }> => api.post(`/cluster-action/trades/${tradeId}/clusters/close`, { pair_tag, side }),

  deleteSignal: async (
    tradeId: number,
    signalId: number,
  ): Promise<{ deleted: boolean; signal_id: number; pair_tag: string; side: string }> =>
    api.delete(`/cluster-action/trades/${tradeId}/signals/${signalId}`),

  getRecent: async (tradeId: number): Promise<{ recent: any[] }> =>
    api.get(`/cluster-action/trades/${tradeId}/recent`),
};

// ─── Maximisation (pair-3 selector + minima balancer) ────────────────────────

export const maximisationApi = {
  getConfig: async (): Promise<any> =>
    api.get('/maximisation/config'),

  getActiveTrades: async (): Promise<{
    trades: any[];
    spot: number;
    engine: any;
  }> => api.get('/maximisation/active-trades'),

  getTradeConfig: async (tradeId: number): Promise<{
    trade_id: number;
    minima_ratio: number;
    enabled: boolean;
  }> => api.get(`/maximisation/trades/${tradeId}/config`),

  updateTradeConfig: async (
    tradeId: number,
    payload: { enabled: boolean },
  ): Promise<{
    trade_id: number;
    minima_ratio: number;
    enabled: boolean;
  }> => api.patch(`/maximisation/trades/${tradeId}/config`, payload),

  getSignals: async (tradeId: number): Promise<{ signals: any[] }> =>
    api.get(`/maximisation/trades/${tradeId}/signals`),

  getRecent: async (tradeId: number): Promise<{ recent: any[] }> =>
    api.get(`/maximisation/trades/${tradeId}/recent`),

  evaluateNow: async (tradeId: number): Promise<any> =>
    api.post(`/maximisation/trades/${tradeId}/evaluate`),

  /** Dry-run Phase A for strikes the trader typed: climb plan, extrema,
   *  ratio and money for THAT pair. Writes nothing. */
  previewStrikes: async (
    tradeId: number,
    strikes: { ce_strike: number; pe_strike: number },
  ): Promise<{
    trade_id: number; spot: number | null; state: string;
    strikes: { ce: number; pe: number }; evaluated_at: string;
    gate?: string; reason?: string;
    phase_a?: any;
  }> => api.post(`/maximisation/trades/${tradeId}/preview-strikes`, strikes, {
    // A check rebuilds every unit's roll simulations (~25 s idle, more while
    // the 60 s loop is mid-evaluation) — the instance default of 30 s cut
    // the first one off (owner 2026-09-21, "timeout of 30000ms exceeded").
    timeout: 150_000,
  }),

  executeSignal: async (
    tradeId: number,
    mode: 'sell_assign' | 'sell_assign_climb_crawlers' | 'sell_assign_climb_all',
    strikes?: { ce_strike: number; pe_strike: number } | null,
  ): Promise<{
    trade_id: number;
    mode: string;
    success: boolean;
    steps: Array<Record<string, any>>;
  }> => api.post(`/maximisation/trades/${tradeId}/execute`, { mode, ...(strikes ?? {}) }),
};

// ─── Pair-2 Auto (executes the pair-2 sell signal on armed trades) ──────────

export const pair2AutoApi = {
  getConfig: async (): Promise<any> =>
    api.get('/pair2-auto/config'),

  getTradeConfig: async (tradeId: number): Promise<any> =>
    api.get(`/pair2-auto/trades/${tradeId}/config`),

  updateTradeConfig: async (
    tradeId: number,
    payload: { enabled?: boolean; include_backstop?: boolean },
  ): Promise<any> => api.patch(`/pair2-auto/trades/${tradeId}/config`, payload),

  getRuns: async (tradeId: number): Promise<{ trade_id: number; runs: any[] }> =>
    api.get(`/pair2-auto/trades/${tradeId}/runs`),

  /** No strikes → the signal's offered straddle; both strikes → those (owner 2026-10-14). */
  execute: async (tradeId: number, strikes?: { ce_strike: number; pe_strike: number }): Promise<{
    trade_id: number;
    trigger: string;
    success: boolean;
    complete: boolean;
    steps: Array<Record<string, any>>;
  }> => api.post(`/pair2-auto/trades/${tradeId}/execute`, strikes ?? null),

  /** Live bids for a pair-2 CE / PE pick — nothing written. */
  preview: async (tradeId: number, strikes: { ce_strike: number; pe_strike: number }): Promise<{
    expiry: string; quantity: number; ce_strike: number; pe_strike: number;
    ce_bid: number | null; pe_bid: number | null; credit_per_share: number | null; credit_total: number | null;
    offered_strike: number | null; spot: number | null; missing: string[];
  }> => api.post(`/pair2-auto/trades/${tradeId}/preview`, strikes),
};

// ─── Expiry Transfer (move crawler buy legs to another expiry, same strike) ──

export interface ExpiryTransferRequest {
  crawler_ids: number[];
  leverage_buy_ids?: number[];
  call_expiry?: string | null;
  put_expiry?: string | null;
  /** strikes further OTM on the new expiry, 0..10 (owner 2026-11-03) */
  call_shift?: number;
  put_shift?: number;
}

export const expiryTransferApi = {
  getTrades: async (): Promise<{
    trades: any[];
    spot: number;
    expiries: string[];
  }> => api.get('/expiry-transfer/trades'),

  preview: async (tradeId: number, payload: ExpiryTransferRequest): Promise<any> =>
    api.post(`/expiry-transfer/trades/${tradeId}/preview`, payload),

  scan: async (
    tradeId: number,
    payload: { crawler_ids: number[]; leverage_buy_ids?: number[]; min_ratio: number; max_shift?: number },
  ): Promise<any> =>
    api.post(`/expiry-transfer/trades/${tradeId}/scan`, payload),

  execute: async (tradeId: number, payload: ExpiryTransferRequest): Promise<{
    trade_id: number;
    success: boolean;
    transferred: number;
    total_extra_debit: number;
    steps: Array<Record<string, any>>;
  }> => api.post(`/expiry-transfer/trades/${tradeId}/execute`, payload),
};

// ─── Last Action (clone of Least Action for testing a new process) ───────────

export const lastActionApi = {
  getConfig: async (): Promise<any> =>
    api.get('/last-action/config'),

  getActiveTrades: async (): Promise<{
    trades: any[];
    spot: number;
    range_pct: number;
  }> => api.get('/last-action/active-trades'),

  getTradePayoff: async (tradeId: number): Promise<{
    trade_id: number;
    spot: number;
    range_pct: number;
    payoff_points: { underlying_price: number; pl: number }[];
    max_profit: number | null;
    max_loss: number | null;
    breakevens: number[];
    extrema: any;
    positions: any[];
  }> => api.get(`/last-action/trades/${tradeId}/payoff`),

  getSignals: async (
    tradeId: number,
    operation?: 'call' | 'put',
    limit = 100,
    offset = 0,
  ): Promise<{ signals: any[] }> =>
    api.get(`/last-action/trades/${tradeId}/signals`, {
      params: { operation, limit, offset },
    }),

  getRecent: async (tradeId: number): Promise<{ recent: any[] }> =>
    api.get(`/last-action/trades/${tradeId}/recent`),

  deleteSignal: async (
    tradeId: number,
    signalId: number,
  ): Promise<{ deleted: boolean; signal_id: number; exited_main_strike: number }> =>
    api.delete(`/last-action/trades/${tradeId}/signals/${signalId}`),

  getTradeConfig: async (tradeId: number): Promise<{
    trade_id: number;
    range_pct: number;
    delta_threshold: number;
    shelf_threshold: number;
    call_mode: 'exit_and_sell' | 'sell_only';
    put_mode: 'exit_and_sell' | 'sell_only';
    call_include_opposite: boolean;
    put_include_opposite: boolean;
    defaults: { range_pct: number; delta_threshold: number; shelf_threshold: number };
  }> => api.get(`/last-action/trades/${tradeId}/config`),

  updateTradeConfig: async (
    tradeId: number,
    payload: {
      range_pct?: number; delta_threshold?: number; shelf_threshold?: number;
      call_mode?: 'exit_and_sell' | 'sell_only';
      put_mode?: 'exit_and_sell' | 'sell_only';
      call_include_opposite?: boolean;
      put_include_opposite?: boolean;
    },
  ): Promise<{
    trade_id: number;
    range_pct: number;
    delta_threshold: number;
    shelf_threshold: number;
    call_mode: 'exit_and_sell' | 'sell_only';
    put_mode: 'exit_and_sell' | 'sell_only';
    call_include_opposite: boolean;
    put_include_opposite: boolean;
  }> => api.patch(`/last-action/trades/${tradeId}/config`, payload),
};

export default api;

// ============================================================================
// Task board API
// ============================================================================
// One-for-one with `backend/app/api/tasks.py`. Two things about the shapes are
// worth knowing at every call site:
//
//   · Reads answer `{ tasks, foreign_tasks }`. `foreign_tasks` is every row the
//     fetch NAMES as a prerequisite or a dependent but does not itself contain
//     — a task on another board, or one filtered out. Without it a "Waiting on
//     …" chip would answer only when both ends happened to land in the same
//     fetch, so it would appear and vanish depending on which screen you stood
//     on. Never drop it.
//
//   · Errors are `{ detail }`, and a schema violation is a 422 whose `detail`
//     is a LIST of field errors rather than a sentence. `taskError()` below
//     flattens both into something showable.

export const tasksApi = {
  /**
   * Who a task may be given to. Its own route rather than `usersApi.listUsers`,
   * which is admin-only — every desk that works the board needs the roster to
   * assign anything.
   */
  getPeople: async (): Promise<{ people: PlatformUser[] }> => api.get('/platform/tasks/people'),

  // ── Boards ──────────────────────────────────────────────────────────────
  getBoards: async (): Promise<{ boards: TaskBoard[] }> => api.get('/platform/tasks/boards'),

  createBoard: async (body: {
    name: string;
    description?: string | null;
    color?: string | null;
    owner_user_id?: number | null;
  }): Promise<{ board: TaskBoard }> => api.post('/platform/tasks/boards', body),

  updateBoard: async (
    id: number,
    // ABSENT means "leave alone" and NULL means "clear" — the board bar sends
    // only what was touched, so renaming must not blank the owner, and picking
    // "No owner" has to be able to actually remove one. Never spread a whole
    // board object in here.
    body: Partial<{
      name: string;
      description: string | null;
      color: string | null;
      is_archived: boolean;
      owner_user_id: number | null;
    }>
  ): Promise<{ board: TaskBoard }> => api.post(`/platform/tasks/boards/${id}`, body),

  deleteBoard: async (id: number): Promise<{ deleted: boolean; tasks_deleted: number }> =>
    api.delete(`/platform/tasks/boards/${id}`),

  /** The whole live rail, renumbered — see the endpoint for why not just the pair. */
  reorderBoards: async (updates: { id: number; sort_order: number }[]): Promise<{ updated: number }> =>
    api.post('/platform/tasks/boards/reorder', { updates }),

  // ── Tasks ───────────────────────────────────────────────────────────────
  getTasks: async (boardId: number): Promise<TaskListResponse> =>
    api.get('/platform/tasks', { params: { board_id: boardId } }),

  /** One person across every live board. `'none'` is the work nobody owns yet. */
  getTasksForPerson: async (assignee: number | 'none'): Promise<TaskListResponse> =>
    api.get('/platform/tasks', { params: { assignee_user_id: assignee } }),

  createTask: async (body: TaskInput & { board_id: number; title: string }): Promise<{ task: Task }> =>
    api.post('/platform/tasks', body),

  updateTask: async (id: number, body: TaskInput): Promise<{ task: Task }> =>
    api.post(`/platform/tasks/${id}`, body),

  deleteTask: async (id: number): Promise<{ deleted: boolean }> =>
    api.delete(`/platform/tasks/${id}`),

  /**
   * A whole column after a drag. Answers with how many rows it WROTE, and 409s
   * when that is fewer than were sent — a bulk write that silently skipped a
   * deleted row would report success on a move that never happened.
   */
  reorderTasks: async (updates: TaskReorderUpdate[]): Promise<{ updated: number }> =>
    api.post('/platform/tasks/reorder', { updates }),

  // ── Checklist ───────────────────────────────────────────────────────────
  addSubtask: async (taskId: number, title: string): Promise<{ subtask: TaskSubtask }> =>
    api.post(`/platform/tasks/${taskId}/subtasks`, { title }),

  updateSubtask: async (
    taskId: number,
    subtaskId: number,
    body: Partial<{ title: string; is_done: boolean; sort_order: number }>
  ): Promise<{ subtask: TaskSubtask }> =>
    api.post(`/platform/tasks/${taskId}/subtasks/${subtaskId}`, body),

  deleteSubtask: async (taskId: number, subtaskId: number): Promise<{ deleted: boolean }> =>
    api.delete(`/platform/tasks/${taskId}/subtasks/${subtaskId}`),

  // ── Dependencies ────────────────────────────────────────────────────────
  linkDependency: async (taskId: number, dependsOnTaskId: number): Promise<{ linked: boolean }> =>
    api.post(`/platform/tasks/${taskId}/dependencies`, { depends_on_task_id: dependsOnTaskId }),

  // DELETE carries a body here, matching the endpoint. axios needs it under
  // `data:` — passing it as the second argument silently sends nothing.
  unlinkDependency: async (taskId: number, dependsOnTaskId: number): Promise<{ unlinked: boolean }> =>
    api.delete(`/platform/tasks/${taskId}/dependencies`, {
      data: { depends_on_task_id: dependsOnTaskId },
    }),

  // ── Discussion ──────────────────────────────────────────────────────────
  getComments: async (taskId: number): Promise<{ comments: TaskComment[] }> =>
    api.get(`/platform/tasks/${taskId}/comments`),

  addComment: async (taskId: number, body: string): Promise<{ comment: TaskComment }> =>
    api.post(`/platform/tasks/${taskId}/comments`, { body }),

  editComment: async (taskId: number, commentId: number, body: string): Promise<{ comment: TaskComment }> =>
    api.post(`/platform/tasks/${taskId}/comments/${commentId}`, { body }),

  deleteComment: async (taskId: number, commentId: number): Promise<{ deleted: boolean }> =>
    api.delete(`/platform/tasks/${taskId}/comments/${commentId}`),

  // ── Attachments ─────────────────────────────────────────────────────────
  getFiles: async (taskId: number): Promise<{ files: TaskFile[] }> =>
    api.get(`/platform/tasks/${taskId}/files`),

  uploadFile: async (taskId: number, file: File): Promise<{ file: TaskFile }> => {
    const form = new FormData();
    form.append('file', file);
    // The Content-Type header must be UNSET, not set to multipart: the browser
    // has to add its own boundary, and the axios default of application/json
    // would leave the body unparseable server-side.
    return api.post(`/platform/tasks/${taskId}/files`, form, {
      headers: { 'Content-Type': undefined as any },
    });
  },

  deleteFile: async (taskId: number, fileId: number): Promise<{ deleted: boolean }> =>
    api.delete(`/platform/tasks/${taskId}/files/${fileId}`),

  /**
   * Fetch the bytes and hand them to the browser as a download.
   *
   * NOT a plain <a href> — the route needs the bearer token, and a link carries
   * no Authorization header, so it would 401. Putting the token in the query
   * string instead would write it into history and any access log it passes.
   */
  downloadFile: async (taskId: number, fileId: number, name: string): Promise<void> => {
    const blob: Blob = await api.get(`/platform/tasks/${taskId}/files/${fileId}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoked on the next tick: revoking synchronously can beat the click in
    // Safari and the download arrives empty.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  // ── Activity trail ──────────────────────────────────────────────────────
  getEvents: async (taskId: number): Promise<{ events: TaskEvent[] }> =>
    api.get(`/platform/tasks/${taskId}/events`),
};

/**
 * The sentence to show for a failed task call.
 *
 * The board's refusals are WRITTEN TO BE READ — "Work on this board is signed
 * off by Priya. Move it to Quality Check and it will reach them." — so throwing
 * away `detail` in favour of "Request failed" would discard the whole point of
 * them. Three shapes arrive:
 *
 *   · `{ detail: "a sentence" }`            — the handlers' own refusals
 *   · `{ detail: { message, updated } }`    — a partial reorder
 *   · `{ detail: [{ msg, loc }, …] }`       — a 422 from schema validation
 */
export const taskError = (err: any, fallback = 'That did not save.'): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    // Pydantic prefixes its own message with "Value error, "; the half after it
    // is the sentence we wrote.
    const msg = String(first?.msg ?? '').replace(/^Value error,\s*/, '');
    return msg || fallback;
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  return fallback;
};

// ── Trade Initiation v2 — the structure derived from the chain ──────────
// ONE expiry in (the main sell, M); K1, K2, every CC / LB / LC strike and the
// payoff out, for the two cases the owner fixed on 2026-09-22. The CC always
// buys the nearer expiry and sells the next one out; M+1 and M+2 are the next
// two LISTED expiries. Preview only — nothing is executed from this page yet.

/** near_main = CC M/M+1, longs on M · far_main = CC M+1/M+2, longs on M.
 *  The owner dropped both combinations with the longs on M+1. */
export type TiV2CaseKey = 'near_main' | 'far_main';

export interface TiV2Side {
  cc_buy: number; cc_sell: number; gap_pts: number;
  lb: number; lc_buy: number; lc_sell: number | null;
  lc_sell_net_financing: number;
  ratio: number; meets: boolean; target_ratio: number;
  peak_after_cost: number; wing_min: number; cost_per_share: number;
  branch: 'up' | 'down';
  cc_buy_delta: number; cc_sell_delta: number;
  implied_p1: number; implied_p2: number;
  prices: { cc_buy_ask: number; cc_sell_bid: number; lb_ask: number; lc_buy_ask: number; lc_sell_bid: number | null };
}

export interface TiV2Branch {
  points: Array<{ x: number; pl: number }>;
  peak: number; peak_at: number;
  /** minima beyond K2 down / K2 up and the maximum between them, each with the price it sits at */
  left_min: number | null; left_min_at: number | null;
  right_min: number | null; right_min_at: number | null;
  central_max: number | null; central_max_at: number | null;
  /** prices where the book crosses zero at the main expiry */
  breakevens: number[];
}

export interface TiV2Leg {
  role: string; side: 'call' | 'put' | 'both'; action: 'BUY' | 'SELL';
  strike: number; expiry: string; qty: number;
  /** per share (sells at bid, buys at ask), per contract (× multiplier), and signed cash for the qty */
  price: number; per_contract: number; cash: number;
  /** 'both' = in the book whichever way the market goes; pairs 2/3 are one branch each */
  branch: 'up' | 'down' | 'both';
  note: string | null;
}

export interface TiV2Scenario {
  level: number; move: number; label: string;
  up: number; up_cash: number; down: number; down_cash: number;
}

export interface TiV2Cash { shorts_credit: number; protection: number; net_credit: number; peak: number }

export interface TiV2Rung {
  target: number; cost: number; peak: number; wing_min: number | null; reversal: number | null;
  /** $ of wing loss removed per $1 of extra protection vs the rung before; null on the first rung or a free step */
  cover_per_dollar: number | null; free: boolean; chosen: boolean; stopped: boolean;
}

export interface TiV2Case {
  lc_expiry: string;
  /** set on a previewed case, absent on a manual evaluation */
  key?: TiV2CaseKey; label?: string; cc_case?: 'near' | 'far'; lc_slot?: 'main' | 'plus_1';
  cc_buy_expiry?: string; cc_sell_expiry?: string;
  target_ratio: number;
  /** credit in the reversal's branch (what the warning compares against) */
  credit_per_share: number;
  credit_by_branch: { up: number; down: number };
  /** the worst case: pairs 2/3 sold at K2 one way, market runs through the other side */
  reversal: { loss: number | null; at: number | null; branch: 'up' | 'down' | null; exceeds_credit: boolean };
  /** how the wing was picked — the §3.12 ladder, or a fixed ratio */
  pick: { mode: 'cover' | 'ratio' | 'cross'; floor: number | null; target_ratio: number; ladder: TiV2Rung[] };
  call: TiV2Side; put: TiV2Side;
  total_cost_per_share: number;
  peak_after_cost: number;
  /** cash collected at open per share: straddle at bid less every wing leg at its side of the spread */
  open_credit_per_share: number;
  payoff: { up: TiV2Branch; down: TiV2Branch };
  /** contract multiplier (100 for SPX) — every *_cash / per_contract figure is at this */
  multiplier: number;
  legs: TiV2Leg[];
  cash: { up: TiV2Cash; down: TiV2Cash };
  scenarios: TiV2Scenario[];
  /** Present on a solved case; absent on a manual evaluation. */
  search?: {
    call?: { combos: number; meet: number; seconds: number };
    put?: { combos: number; meet: number; seconds: number };
    /** Alternation passes run, and whether the two sides stopped moving. */
    passes?: number; converged?: boolean;
  };
}

/** Owner 2026-09-23: each case also solved at the OTHER case's chosen ratio,
 *  so one row compares the two placements at the same ratio. */
export interface TiV2CrossCell {
  own: boolean;
  peak_after_cost: number; total_cost_per_share: number;
  call: { ratio: number; meets: boolean }; put: { ratio: number; meets: boolean };
  /** the full book of a cross solve; null on the case's own cell (that is plan.cases[key]) */
  case: TiV2Case | null;
}
export interface TiV2CrossRow {
  target_ratio: number; chosen_by: TiV2CaseKey[]; best_peak: TiV2CaseKey;
  cells: Record<TiV2CaseKey, TiV2CrossCell>;
}
export interface TiV2Cross { same_rung: boolean; targets: number[]; rows: TiV2CrossRow[] }

export interface TiV2Plan {
  asof: string; underlying: string; spot: number; grid: number;
  /** preview returns main / plus_1 / plus_2; evaluate returns the chain's own cc_buy / cc_sell */
  expiries: { main: string; plus_1?: string; plus_2?: string; cc_buy?: string; cc_sell?: string;
              days: Record<string, number> };
  step1: { forward: number; k1: number; call_bid: number; put_bid: number;
           call_delta: number; put_delta: number; straddle: number };
  step2: { worst_atm: number; trigger_pts: number; k2_up: number; k2_down: number;
           credit: number; gross_peak: number; trigger_factor: number; revenue_factor: number;
           /** per branch: pairs 2/3 sold on the way down keep 90% of pair 1, on the way up 75% */
           worst_atm_by_branch: { up: number; down: number }; trigger_by_branch: { up: number; down: number };
           credit_by_branch: { up: number; down: number }; gross_peak_by_branch: { up: number; down: number };
           revenue_factor_by_branch: { up: number; down: number } };
  target_ratio: number;
  mode: 'cover' | 'ratio';
  cover_floor: number;
  case_order: TiV2CaseKey[];
  cases: Record<TiV2CaseKey, TiV2Case>;
  /** which of the three costs least in protection, and which peaks highest after it */
  cheapest: TiV2CaseKey;
  /** present on a preview; absent on an evaluation */
  cross?: TiV2Cross;
  best_peak: TiV2CaseKey;
  seconds: number;
}

export interface TiV2Strikes {
  cc_buy: number; cc_sell: number; lb: number; lc_buy: number; lc_sell?: number | null;
}

/** `evaluate` returns the header plus ONE case on the typed strikes. */
export interface TiV2Evaluation extends Omit<TiV2Plan, 'cases' | 'case_order' | 'cheapest' | 'best_peak'> {
  case: TiV2Case;
}

/** D34 premium sacrifice at a chosen delta, raw (before the page's factor). */
export interface TiV2PsSide {
  ps: number | null; reason?: string;
  skew_ratio?: number; strike?: number; iv?: number; abs_delta?: number; market_mid?: number; interpolated?: boolean;
}
export interface TiV2PsSet {
  expiry: string; days: number; atm_strike: number | null; atm_option: number | null; atm_iv: number | null;
  call: TiV2PsSide; put: TiV2PsSide;
}
export interface TiV2Ps {
  asof: string; spot: number; case: TiV2CaseKey; label: string;
  /** whole-delta units (1.0 = |delta| 0.01) and the fraction */
  delta: number; unit_delta: number; k: number; factor_default: number;
  /** the CC on its buy expiry; the LB and LC on M (the same chain in the near case) */
  sets: { cc: TiV2PsSet; lb_lc: TiV2PsSet };
  seconds: number;
}

export interface TiV2ExecuteBody {
  main_expiry: string; case: TiV2CaseKey;
  call: Required<TiV2Strikes>; put: Required<TiV2Strikes>;
  quantity: number;
  ps_call: number; ps_put: number; ps_lc_call: number; ps_lc_put: number; ps_lb_call: number; ps_lb_put: number;
  expected_open_credit_per_share?: number;
  accept_drift?: boolean;
  target_ratio?: number;
}

export interface TiV2ExecuteResult {
  executed: boolean;
  reason?: 'plan_moved' | 'write_failed' | string;
  drift?: string[];
  failed_step?: string;
  detail?: string;
  message?: string;
  trade_id?: number;
  case?: TiV2CaseKey;
  crawler_ids?: number[];
  lc_ids?: number[];
  leverage_buy_id?: number;
  steps?: { step: string; status: string; [k: string]: any }[];
  complete?: boolean;
  /** the header plus the case re-priced at execute time */
  plan: TiV2Evaluation;
  seconds?: number;
}

export const tradeInitiationV2Api = {
  /** PS at a delta for one case: the CC on its buy expiry, the LB/LC on M. */
  ps: async (params: { main_expiry: string; case: TiV2CaseKey; delta: number }): Promise<TiV2Ps> =>
    api.get('/trade-initiation/v2/ps', { params, timeout: 60_000 }),

  /** Open one previewed case: protection first, then the straddle, one transaction, DB only. */
  execute: async (body: TiV2ExecuteBody): Promise<TiV2ExecuteResult> =>
    api.post('/trade-initiation/v2/execute', body, { timeout: 120_000 }),

  preview: async (params: { main_expiry: string; target_ratio?: number;
                            mode?: 'cover' | 'ratio'; cover_floor?: number }): Promise<TiV2Plan> =>
    // Two exhaustive searches run server-side (~10–60 s on a busy box).
    api.get('/trade-initiation/v2/preview', { params, timeout: 180_000 }),

  /** Price typed strikes on a fresh chain — no search. Same shape as a solved case. */
  evaluate: async (params: {
    main_expiry: string; buy_expiry: string; sell_expiry: string; lc_expiry: string; target_ratio?: number;
    call: TiV2Strikes; put: TiV2Strikes;
  }): Promise<TiV2Evaluation> =>
    api.get('/trade-initiation/v2/evaluate', {
      params: {
        main_expiry: params.main_expiry, buy_expiry: params.buy_expiry, sell_expiry: params.sell_expiry,
        lc_expiry: params.lc_expiry, target_ratio: params.target_ratio,
        call_cc_buy: params.call.cc_buy, call_cc_sell: params.call.cc_sell, call_lb: params.call.lb,
        call_lc_buy: params.call.lc_buy, call_lc_sell: params.call.lc_sell ?? undefined,
        put_cc_buy: params.put.cc_buy, put_cc_sell: params.put.cc_sell, put_lb: params.put.lb,
        put_lc_buy: params.put.lc_buy, put_lc_sell: params.put.lc_sell ?? undefined,
      },
      timeout: 60_000,
    }),
};

/** One unit's PS fallback right now (owner 2026-10-05): ATM option × K(δ) × skew ratio on its buy expiry. */
export interface PsReading {
  ps: number | null; atm_option?: number | null; atm_iv?: number | null; k?: number | null;
  strike?: number | null; iv?: number | null; skew_ratio?: number | null; beyond?: boolean | null;
  limit_10: number | null;
}
export interface PsLiveUnit {
  uid: string; unit: string; kind: 'MC' | 'LC' | 'LB'; side: 'call' | 'put'; delta: number;
  buy_strike: number; expiry: string; qty: number;
  /** What is stored on the unit — the PS the rule uses — with the delta and time it was calculated at. */
  stored: PsReading & { delta: number | null; at: string | null; expiry?: string };
  in_use: { ps: number; source: 'stored' | 'delta' | 'last_good' | 'default'; delta: number | null } | null;
  /** Only on a live read (Read now, or a previewed delta): what it would be at this moment. */
  now: (PsReading & { reason?: string | null; read_at?: string | null }) | null;
}
/** Lambda Algo page (owner 2026-10-05). */
export interface LambdaAlgoTrade { trade_id: number; serial: string | null; minutes: number; last: string | null }
export interface LambdaPoint { price: number; pl: number }
export interface LambdaMove { unit: string; uid: string; side: 'call' | 'put'; from: number; to: number; strikes: number; cost: number }
/** Owner 2026-10-10 (Final rule): every climb is scored on the worst loss, ΔL − λ × cost, one λ per check. */
export type LambdaState = 'loss' | 'comfortable' | 'profit';
export interface LambdaCombo {
  phase: 'A' | 'C' | 'other'; kind: 'single' | 'group' | 'cross'; side: 'call' | 'put' | 'both'; lambda: number;
  state: LambdaState; climb: LambdaMove[]; cost: number; rise: number; needed: number; net: number; ratio: number | null;
  passes: boolean; after: { put: LambdaPoint | null; call: LambdaPoint | null; peak: LambdaPoint | null };
  engine_pick?: boolean;
}
export interface LambdaPhase {
  side: 'call' | 'put' | 'both'; combinations: number; nodes: number; priced: number; passing_seen: number; capped: boolean;
  top: LambdaCombo[];
}
export interface LambdaUnit {
  uid: string; unit: string; kind: 'MC' | 'LC' | 'LB'; side: 'call' | 'put'; which: 'worse' | 'other';
  from: number | null; expiry: string | null;
  qty: number | null; ps: number | null; sizes: number; passing: number; best: LambdaCombo | null;
}
export interface LambdaMinute {
  time: string; minute: string; trade_id: number; serial: string | null; cooling_since: string | null;
  rule_on: boolean; source: 'engine' | 'fresh'; elapsed_s: number;
  settings: { lam_worse: number; lam_comfort: number; lam_profit: number; comfort_ratio: number; pick: 'marginal' | 'average' };
  engine: { climb_now: { step: string; side: string; units: LambdaMove[]; cost: number; rise: number | null; net: number | null } | null;
    outcome: { method: string; executed: { uid: string; unit: string; from: number; to: number }[]; refused: string[] } | null };
  mode: 'marginal' | 'average'; spot: number; target: string;
  before: { put: LambdaPoint | null; call: LambdaPoint | null; peak: LambdaPoint | null };
  worse_side: 'call' | 'put' | null; check_lambda: number; state: LambdaState; worst: number | null;
  phases: Partial<Record<'A' | 'C', LambdaPhase>>;
  units: LambdaUnit[]; top: LambdaCombo[];
}

export interface PsLive {
  trade_id: number; serial?: string; deltas: { call: number; put: number }; saved_deltas: { call: number; put: number };
  spot: number | null; live: boolean; fixed?: boolean;
  rule_on: boolean; cooling_since: string | null; units: PsLiveUnit[]; reason?: string;
}

/** One trade's wing climb rule settings (nightingale_climb_settings). */
export interface ClimbSettings {
  trade_id: number;
  enabled: boolean;
  lam_worse: number;
  lam_comfort: number;
  lam_profit: number;
  comfort_ratio: number;
  pick: 'marginal' | 'average';
  /** Columns kept from the pre-2026-10-12 rule; no longer used. */
  lam_other?: number;
  switch_gap?: number;
  /** PS fallback from delta per side (owner 2026-10-05), in delta points: 0.35 = |Δ| 0.0035. */
  ps_delta_call: number;
  ps_delta_put: number;
  updated_at: string;
  /** Only on a save that changed a PS delta: the PS recalculated there and then. */
  ps_recalc?: { updated: number; kept: number; reason?: string;
    units: Record<string, { ps: number; source: 'stored' | 'delta' | 'last_good' | 'default'; delta: number | null; reason?: string | null }> };
}
